import React, { useEffect, useState, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import i18n from "i18n";
import FileSaver from "file-saver";
import { el } from "date-fns/locale";

import { StockService } from "services/StockService";
import { WSService } from "services/WSServices";
import { LocationService } from "services/LocationService";
import { CategoryService } from "services/CategoryService";

import useRequest from "hooks/useRequest";

import { stockByItem } from "helpers/stock/StockHelper";
import { EventRepository } from "helpers/EventRepository";
import { isMobile } from "helpers/Mobile";

import AdminLayout from "components/MainApp/layouts/DesktopLayout";
import Loader from "components/MainApp/atoms/Loader";
import Tooltip from "components/MainApp/atoms/Tooltip";
import ErrorModal from "components/MainApp/organisms/Stock/ErrorModalAuditory";
import { NotFoundItems } from "views/Admin/components/NotFoundItems";

import AuditoriaFilterPanel from "../AuditoriaFilterPanel";
import ReadingPanel from "../ReadingPanel";
import ItemStockReadingTable from "../ItemStockReadingTable";

import excel from "assets/images/excel.svg";
import excelwhite from "assets/images/excelwhite.svg";
import styles from "./CreateStockAudit.module.scss";

const headers = {
  product: {
    title: i18n.t("product").toUpperCase(),
    active: false,
    direction: false
  },
  sku: {
    title: i18n.t("sku").toUpperCase(),
    active: false,
    direction: false
  },
  tags: {
    title: i18n.t("tags").toUpperCase(),
    active: false,
    direction: false
  }
};

const CreateStockAudit = ({ user }) => {
  const history = useHistory();
  const headerOptions = [];

  const globalStockRef = useRef([]);

  const {
    loading,
    beforeSubmit,
    afterSubmit,
    errors,
    dealWithError
  } = useRequest();
  const [enabledRead, setEnabledRead] = useState(false);

  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [mobilePanelOpened, setMobilePanelOpened] = useState(true);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [originalStock, setOrginalStock] = useState([]);

  const [groupedStock, setGroupedStock] = useState([]);
  const [groupedStockAll, setGroupedStockAll] = useState([]);
  const [tagsInMov, setTagsInMov] = useState([]);
  const [validatedItems, setValidatedItems] = useState(0);
  const [invalidTagsCount, setInvalidTagsCount] = useState(0);
  const [msgTooltip, setMsgToolTip] = useState(i18n.t("select.filters"));
  const [selectedCombo, setSelectedCombo] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState("0.5");
  const [productFiltered, setProductFiltered] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filterProductsCombo, setFilterProductsCombo] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [stockCountTotal, setStockCountTotal] = useState(0);
  const tagNotAssociated = i18n.t("not.have.assigned.location");
  const [searched, setSearched] = useState(false);
  const [categoriesTree, setCategoriesTree] = useState([]);
  false;
  const [clear, setClear] = useState(false);
  const [locationIds, setLocationIds] = useState([]);
  const [auditDetails, setAuditDetails] = useState([]);

  const businessId = user?.roles[0]?.business_id;
  const userId = user?.id;

  useEffect(() => {
    beforeSubmit();
    CategoryService.categories_tree()
      .then(res => {
        setCategoriesTree(res);
        afterSubmit();
      })
      .catch(_ => {
        afterSubmit();
      });
  }, []);

  useEffect(() => {
    const newStockCount = originalStock.length;
    setStockCountTotal(newStockCount);
  }, [originalStock]);

  useEffect(() => {
    const newStockCount = groupedStock.reduce(
      (total, item) => total + item.expectedUnits,
      0
    );
    setStockCountTotal(newStockCount);
  }, [groupedStock]);

  const search = selectedLoc => {
    if (!selectedLoc) return;

    // --- PREVENTIVE CLEANING BEFORE NEW SEARCH ---
    setAuditDetails([]); // Clear accumulated audit details
    setOrginalStock([]); // Clear previous stock
    setGroupedStock([]); // Clear previous grid
    globalStockRef.current = []; // Clear mutable reference
    // --------------------------------------------------

    if (selectedLoc.count == 0) {
      setSearched(true);
      clearGrid();
      return;
    }

    setEnabledRead(false);

    const params = {
      no_page: 1,
      status: "LOC"
    };
    if (selectedLoc.count > 1) {
      params.locations = selectedLoc.results.map(n => n.id);
    } else {
      params.location = selectedLoc?.id ?? selectedLoc.results[0]?.id;
    }

    // Add categories and products to the filters if available.
    let skus = [];
    if (selectedProducts?.length > 0) {
      skus = selectedProducts.map(product => product.sku);
    }

    // Filter the categories that contain the selected products
    const filteredCategories = selectedCategories.filter(category => {
      const categoryProductSkus = Array.isArray(category?.products)
        ? category?.products.map(product => product?.sku)
        : [];
      const hasSelectedProduct = selectedProducts.some(product =>
        categoryProductSkus.includes(product?.sku)
      );
      return !hasSelectedProduct;
    });

    // Filter the subcategories that contain the selected products
    const filteredSubCategories = filteredCategories.map(category => {
      const filteredSubCategories = Array.isArray(category?.subcategories)
        ? category?.subcategories.filter(subCategory => {
            const subCategoryProductSkus = Array.isArray(subCategory?.products)
              ? subCategory.products.map(product => product?.sku)
              : [];
            const hasSelectedProduct = selectedProducts.some(product =>
              subCategoryProductSkus.includes(product?.sku)
            );
            return !hasSelectedProduct;
          })
        : [];
      return { ...category, subcategories: filteredSubCategories };
    });

    if (filteredSubCategories?.length > 0) {
      params.categories = filteredSubCategories.map(category => category.id);
      const selectedProductsFromCategories = filteredSubCategories.flatMap(
        category => category?.products
      );
      const categorySkus = selectedProductsFromCategories.map(
        product => product?.sku
      );
      skus = [...new Set([...skus, ...categorySkus])];
    }

    if (skus.length > 0) {
      params.skus = skus;
    }

    beforeSubmit();
    StockService.stock(params)
      .then(res => {
        setData(res);
        setSearched(true);
      })
      .catch(_ => {
        setSearched(true);
        afterSubmit();
      });
  };

  const setData = res => {
    afterSubmit();
    const _groupedStock = stockByItem(res);
    const products = _groupedStock.map(item => ({
      key: item.sku,
      label: item.title
    }));

    setProductFiltered([]);

    const tags_in_mov = res
      .filter(stock => stock.status === "IN_MOV")
      .map(stock => stock.tag.rfid);
    setTagsInMov(tags_in_mov);
    setOrginalStock(res);
    setGroupedStock(_groupedStock);
    setProductsStock(_groupedStock);
    setValidatedItems(0);
    setInvalidTagsCount(0);
    globalStockRef.current = _groupedStock;
    setAuditDetails([]);
    setEnabledRead(true);
    if (res.length > 0) {
      setMsgToolTip(i18n.t("generate.report"));
      setTooltipStyle("100");
    } else {
      setMsgToolTip(i18n.t("generate.report.error"));
      setTooltipStyle("0.5");
    }
  };

  const validatedItemsCount = () => {
    if (productFiltered?.length > 0) {
      const count = groupedStock.reduce((total, item) => {
        const validTagsCount = item.tags.reduce((tagTotal, tag) => {
          if (tag.selected === true && tag.message === null) {
            return tagTotal + 1;
          }
          return tagTotal;
        }, 0);
        return total + validTagsCount;
      }, 0);
      return count;
    }
    return validatedItems;
  };

  const filterProducts = (
    productFilteredAux,
    groupedStockAllAux = null,
    comboCategory = null,
    comboProducts
  ) => {
    const groupedStockAllAux2 = groupedStockAllAux ?? groupedStockAll;
    setProductFiltered(productFilteredAux);
    let filteredGroupedStock = groupedStockAllAux2;
    if (productFilteredAux.length > 0) {
      filteredGroupedStock = groupedStockAllAux2.filter(item => {
        return productFilteredAux.some(product => product.key === item.sku);
      });
    }

    if (comboCategory) {
      filteredGroupedStock = filteredGroupedStock.filter(item => {
        return comboCategory.some(category => category.id === item.category_id);
      });
    }
    if (comboProducts) {
      filteredGroupedStock = filteredGroupedStock.filter(item => {
        return comboProducts.some(product => product.sku === item.sku);
      });
    }
    setGroupedStock(filteredGroupedStock);
    globalStockRef.current = filteredGroupedStock;
    reloadTotal();
  };

  const receiveEvent = useCallback(payload => {
    const ws = WSService.getInstance();
    // console.log("Message from socket");
    // console.log(payload);
    if (payload.method === "EVENT" && payload.room && ws.inRoom(payload.room)) {
      handleTagSearch(payload.params.epc, payload.params);
    }
  }, []);

  const setErrorModal = message => {
    setErrorMessage(message);
    setOpenErrorModal(true);
  };

  const clearGrid = () => {
    setOrginalStock([]);
    setGroupedStock([]);
    setInvalidTagsCount(0);
    setValidatedItems(0);
    setAuditDetails([]);
    globalStockRef.current = [];
  };

  const handleApplyFilters = arg => {
    // Accept both legacy array (selectedOptions) 
    // and new payload { selectedOptions, status, modifiedTrue..., date_from,... }
    let payload = null;
    if (arg && !Array.isArray(arg) && arg.selectedOptions !== undefined) {
      payload = arg;
    }
    const selectedOptionsNew = payload ? payload.selectedOptions : arg;

    // defensive: ensure selectedOptionsNew is array
    const sel = Array.isArray(selectedOptionsNew)
      ? selectedOptionsNew
      : selectedOptionsNew
      ? [selectedOptionsNew]
      : [];

    setSelectedOptions(sel);

    handleClear();

    // use values from payload if provided (dates/status) else fallback to previous logic
    const statusPayload = payload ? payload.status : undefined;
    const mTruePayload = payload ? payload.modifiedTrue : undefined;
    const mFalsePayload = payload ? payload.modifiedFalse : undefined;
    const dateFromPayload = payload ? payload.date_from : undefined;
    const dateToPayload = payload ? payload.date_to : undefined;

    // keep original behaviour but guarded
    if (sel[1] && Object.keys(sel[1]).length > 0) {
      if (sel.length > 0) {
        setMobilePanelOpened(false);
      }
      var location = {};
      if (sel[2] && Object.keys(sel[2]).length > 0) {
        setSelectedLocation(sel[2]);
        location = sel[2];
        setLocationIds(location.id);
      } else if (sel[1] && Object.keys(sel[1]).length > 0) {
        setSelectedLocation(sel[1]);
        location = sel[1];
        setLocationIds(location.id);
      }

      search(location);
    } else {
      if (sel.length === 0) {
        return;
      }
      // only branch office
      LocationService.locations({
        code: sel[0].id,
        no_parent: "true",
        parent: null
      }).then(locations => {
        if (locations.count > 0) {
          setMobilePanelOpened(false);
        }

        const locationIds = locations.results.map(location => location.id);
        setLocationIds(locationIds);

        setSelectedLocation(locations);
        search(locations);
      });
    }
  };

  const clearProductsStock = () => {
    setGroupedStockAll([]);
    setProductFiltered([]);
  };

  const setProductsStock = _groupedStock => {
    setGroupedStockAll(_groupedStock);
    const products = _groupedStock.map(item => ({
      key: item.sku,
      label: item.title
    }));
  };

  const handleClear = event => {
    const _groupedStock = stockByItem(originalStock);
    setGroupedStock(_groupedStock);
    setGroupedStockAll(_groupedStock);
    filterProducts(productFiltered, _groupedStock);
    setValidatedItems(0);
    setInvalidTagsCount(0);
    globalStockRef.current = _groupedStock;
    setAuditDetails([]);
  };

  const handleDelete = event => {
    clearGrid();
    clearProductsStock();
    setSelectedCombo(null);
    setShowFilters(false);
    setSelectedCategories([]);
    setSelectedProducts([]);
    setSearched(false);
    setMsgToolTip(i18n.t("select.filters"));
    setTooltipStyle("0.5");
    setEnabledRead(false);
  };

  const handleTagSearch = (cod, data = null) => {
    cod = cod?.trim();
    if (!cod) return;

    let found = false;
    // Search EPC in global stock and mark as selected if found
    globalStockRef.current.forEach((i, index, arr) => {
      i.tags.forEach((t, indexTag) => {
        if (t.tag.toUpperCase() === cod.toUpperCase()) {
          if (i.expectedUnits > 0) {
            arr[index]["tags"][indexTag]["selected"] = true;
          }
          found = true;
        }
      });
      if (i.expectedUnits > 0) {
        i.readItems = i.tags.filter(
          t => t.selected === true && t.message === null
        ).length;
      }
    });

    if (found) {
      setGroupedStock(globalStockRef.current);
    }

    if (!found) {
      if (data) {
        addInvalidTag(data);
      } else {
        StockService.localization(cod)
          .then(response => {
            data = {};
            data["stock"] = response.stock;
            data["tag"] = response.tag;
            addInvalidTag(data);
            reloadTotal();
          })
          .catch(error => {
            data = {};
            data["epc"] = cod;
            addInvalidTag(data);
            reloadTotal();
          });
      }
    }

    // Calculate number of validated items and invalid tags 
    // after processing the scanned tag
    reloadTotal();
  };

  const reloadTotal = () => {
    let cant = 0;
    globalStockRef.current.forEach(i => {
      i.tags.forEach(t => {
        if (t.selected && t.message === null) cant++;
      });
    });
    setValidatedItems(cant);
    reorderStock();
    setInvalidTagsCount(getTotalWrongTags());
  };

  /**
   * reordenación del stock para mostrarlos así
   * 1) Auditorías incorrectas
   * 2) Auditorías correcatas
   * 3) Tags erróneos
   */
  const reorderStock = () => {
    moveNotAssociatedTagsToProduct();
    quitDuplicateWrongTagsInGlobalStock();

    const sortedStock = [...globalStockRef.current].sort((a, b) => {
      if (a.readItems === a.expectedUnits && b.readItems !== b.expectedUnits)
        return 1;
      if (a.expectedUnits === 0) return 1;
      if (b.expectedUnits === 0) return -1;
      if (a.readItems !== a.expectedUnits && b.readItems === b.expectedUnits)
        return -1;
      return 0;
    });
    setGroupedStock(sortedStock);
  };

  /**
   * Remove duplicate, incorrect tags from the global stock
   */
  const quitDuplicateWrongTagsInGlobalStock = () => {
    const notWrongItems = [];
    const wrongItemsNotDuplicate = [];
    globalStockRef.current.forEach(item => {
      if (item.expectedUnits > 0) {
        notWrongItems.push(item);
      }
    });
    globalStockRef.current.forEach(item => {
      if (item.expectedUnits === 0) {
        const existingItem = wrongItemsNotDuplicate.find(
          newItem => newItem.sku === item.sku
        );
        if (!existingItem) {
          wrongItemsNotDuplicate.push(item);
        }
      }
    });
    globalStockRef.current = [...notWrongItems, ...wrongItemsNotDuplicate];
  };

  /**
   * Move erroneous items that are not associated with any product to the end of the list
   */
  const moveNotAssociatedTagsToProduct = () => {
    const newGlobalStock = [...globalStockRef.current];
    const newItems = [];

    newGlobalStock.forEach((item, index) => {
      item.tags = item.tags.filter((tag, tagIndex) => {
        if (item.expectedUnits > 0 && tag.message === tagNotAssociated) {
          const existingItem = newItems.find(
            newItem => newItem.sku === item.sku
          );
          if (existingItem) {
            existingItem.tags.push(tag);
          } else {
            const newItem = {
              ...item,
              expectedUnits: 0,
              readItems: 0,
              tags: [tag]
            };
            newItem.id = index + newItems.length;
            newItems.push(newItem);
          }
          return false;
        }
        return true;
      });
    });
    globalStockRef.current = [...newGlobalStock, ...newItems];
  };

  const getTotalWrongTags = () => {
    return globalStockRef.current.reduce((total, item) => {
      if (item.expectedUnits === 0) {
        return total + item.tags.filter(t => t.message !== null).length;
      }
      return total;
    }, 0);
  };

  const addInvalidTag = data => {
    let total = 0;
    let tag = {
      tag: "",
      epc: "",
      barcode: "",
      selected: true,
      message: null
    };
    let item = {
      title: "-- Solo Tag --",
      sku: "",
      image: null
    };
    if (data.stock) {
      item.title = data.stock.item.name;
      item.sku = data.stock.item.sku;
      item.image = data.stock.item.image;
      tag.tag = data.stock.tag.rfid;
      tag.epc = data.stock.tag.rfid;
      tag.barcode = data.stock.tag.barcode;
      if ([].includes(data.stock.status)) {
        tag.message = i18n.t("stock.in.transition");
      } else {
        const location =
          data.stock.location.node.name + " | " + data.stock.location.path_name;
        tag.message = i18n.t("stock.is.located.in") + location;
      }
    } else if (data.tag) {
      item.sku = data.tag.sku;
      item.title = data.tag.variables["name"];
      tag.tag = data.tag.rfid;
      tag.epc = data.tag.rfid;
      tag.barcode = data.tag.barcode;
      tag.message = i18n.t(tagNotAssociated);
    } else {
      item.title = "-- " + i18n.t("epc.only") + " --";
      item.sku = i18n.t("not.associated");
      tag.tag = data.epc;
      tag.epc = data.epc;
      tag.message = i18n.t("tag.isnot.loaded.sistem");
    }

    let found = false;
    globalStockRef.current.forEach((i, index, arr) => {
      if (i.sku === item.sku) {
        i.tags.push(tag);
        if (i.expectedUnits > 0) {
          i.readItems = i.tags.filter(
            t => t.selected && t.message === null
          ).length;
          total += i.tags.filter(t => t.selected === true && t.message !== null)
            .length;
          if (i.title === "-- " + i18n.t("epc.only") + " --") {
            i.title = item.title;
          }
        } else {
          i.readItems = i.tags.length;
        }
        found = true;
      }
    });

    if (!found) {
      total++;
      globalStockRef.current.push({
        title: item.title,
        sku: item.sku,
        image: item.image,
        expectedUnits: 0,
        readItems: 1,
        tags: [tag]
      });
    }
    setInvalidTagsCount(total);
    setGroupedStock(globalStockRef.current);
  };

  // Logic for collecting audit details
  const collectAuditDetails = detailsFromItem => {
    setAuditDetails(prevDetails => {
      const updatedDetails = [...prevDetails];

      detailsFromItem.forEach(newDetail => {
        const index = updatedDetails.findIndex(
          detail => detail.rfid === newDetail.rfid
        );

        if (index !== -1) {
          updatedDetails[index] = {
            ...updatedDetails[index],
            ...newDetail
          };
        } else {
          updatedDetails.push(newDetail);
        }
      });

      return updatedDetails;
    });
  };

  const handleSaveAudit = async () => {
    beforeSubmit();

    const locations = Array.isArray(locationIds) ? locationIds : [locationIds];

    // Rebuild from the current state of view
    const detailsToSend = [];
    (groupedStock || []).forEach(stockItem => {
      (stockItem.tags || []).forEach(tag => {
        const matchingStock = (originalStock || []).find(
          s => s.tag && (s.tag.rfid === tag.tag || s.tag.epc === tag.tag)
        );

        detailsToSend.push({
          rfid: tag.tag,
          product_id: matchingStock ? matchingStock.item.id : null,
          found_status: !!tag.selected,
          modified_status: false,
          location: matchingStock ? matchingStock.location.id : null
        });
      });
    });

    if (detailsToSend.length === 0) {
      afterSubmit();
      setErrorMessage(i18n.t("audit.saved.no.products.error"));
      setOpenErrorModal(true);
      return;
    }

    const auditData = {
      business: businessId,
      user: userId,
      locations: locations,
      details: detailsToSend
    };

    try {
      const response = await StockService.saveAudit(auditData);
      if (response && response.id) {
        // Post-successful save cleanup
        setAuditDetails([]);
        EventRepository.notificationSend({
          label: i18n.t("audit.saved"),
          type: "success"
        });
        setMsgToolTip(i18n.t("select.filters"));
        setTooltipStyle("0.5");
        setEnabledRead(false);
        handleDelete(); // Clear the screen after saving
      } else {
        setErrorMessage(i18n.t("audit.saved.error"));
        setOpenErrorModal(true);
      }
    } catch (error) {
      setErrorMessage(i18n.t("audit.saved.conection.error"));
      setOpenErrorModal(true);
    } finally {
      afterSubmit();
    }
  };

  const getLocationName = () => {
    if (!selectedLocation) return null;

    let locationParts = [];

    // 1. Add Branch (selectedOptions[0])
    if (selectedOptions[0]?.label) {
      locationParts.push(selectedOptions[0].label);
    }

    // 2. Add Zone (selectedOptions[1])
    if (
      selectedOptions[1]?.label &&
      Object.keys(selectedOptions[1]).length > 0
    ) {
      if (locationParts[0] !== selectedOptions[1].label) {
        locationParts.push(selectedOptions[1].label);
      }
    }

    // 3. Add Sector (selectedOptions[2])
    if (
      selectedOptions[2]?.label &&
      Object.keys(selectedOptions[2]).length > 0
    ) {
      locationParts.push(selectedOptions[2].label);
    }

    if (
      selectedLocation.count > 1 &&
      locationParts.length === 1 &&
      selectedOptions[0]?.label
    ) {
      return selectedOptions[0].label;
    }

    if (locationParts.length === 1) {
      return locationParts[0];
    }

    return locationParts.join(" | ");
  };

  const getFilterTitle = () => {
    if (selectedProducts?.length > 0) {
      return selectedProducts[0].label;
    }
    if (selectedCategories?.length > 0) {
      const firstCategoryId = selectedCategories[0];
      return firstCategoryId.name || firstCategoryId.label;
    }
    return null;
  };

  const downloadReport = async () => {
    if (
      !selectedLocation ||
      tooltipStyle === "0.5" ||
      globalStockRef.current.length === 0
    )
      return;

    const items = globalStockRef.current.map(i => ({
      sku: i.sku,
      stock: i.expectedUnits,
      readed: i.tags.filter(s => s.selected).length
    }));

    let loc;
    if (selectedLocation.count > 1) {
      loc = selectedLocation.results.map(n => n.id);
    } else {
      loc = selectedLocation.id ?? selectedLocation.results?.[0]?.id;
    }

    if (!loc) {
      setErrorMessage(i18n.t("error.location.not.found.download.file"));
      setOpenErrorModal(true);
      return;
    }

    beforeSubmit();

    try {

      const blob = await StockService.downloadAuditoryReport(loc, items);
      // Use .xlsx instead .xls because the backend returns an Office Open XML content_type.
      // (spreadsheetml.sheet). Using .xls would cause formatting errors in Excel.
      FileSaver.saveAs(blob, "reporte.xlsx");

      EventRepository.notificationSend({
        label: i18n.t("success.download"),
        type: "success"
      });
    } catch (error) {
      let finalMessage = i18n.t("audit.report.error");

      if (error.response?.data instanceof Blob) {
        try {
          // Using .text() to avoid manual FileReader and directly get the text content of the error response
          const errorText = await error.response.data.text();
          const errorData = JSON.parse(errorText);

          // Serializer dynamic error mapping based on known fields
          if (errorData.location) {
            finalMessage = i18n.t(
              "audit.error.location_required.download.file"
            );
          } else if (errorData.items) {
            finalMessage = i18n.t("audit.error.empty_items.download.file");
          } else if (errorData.detail) {
            finalMessage = errorData.detail;
          } else if (typeof errorData === "object") {
            // Captures other serializer validation errors that didn't map above
            finalMessage = Object.values(errorData).flat()[0] || finalMessage;
          }
        } catch (e) {
          console.warn("Could not parse server error response");
        }
      }

      EventRepository.notificationSend({
        label: i18n.t("error.downloading.report"),
        type: "error"
      });

      setErrorMessage(finalMessage);
      setOpenErrorModal(true);
    } finally {
      afterSubmit();
    }
  };

  const handleSelectionChange = selected => {
    setSelectedCombo(selected);
    setSelectedCategories([]);
    setSelectedProducts([]);
    setFilterProductsCombo([]);
    setSearched(false);
    setClear(true);
    clearGrid();
    if (selectedCombo === selected) return;
    setShowFilters(true);
  };

  const mappedProducts = products => {
    return products.map(product => {
      return {
        id: product.id,
        label: product.name,
        sku: product.sku
      };
    });
  };

  const handleSelectionChangeCategory = selectedCategories => {
    if (!selectedCategories || !Array.isArray(selectedCategories)) {
      setSelectedCategories([]);
      setFilterProductsCombo([]);
      return;
    }

    // Search for the selected categories and subcategories in the categoryTree
    if (selectedCategories.length > 0) {
      setClear(false);
    }
    const selCategories = selectedCategories.map(categoryId => {
      const foundCategory = categoriesTree.find(
        category => category.id === categoryId
      );
      if (foundCategory) {
        return foundCategory;
      }
      const foundSubCategory = categoriesTree
        .flatMap(category => category.subcategories)
        .find(subCategory => subCategory.id === categoryId);
      return foundSubCategory;
    });
    setSelectedCategories(selCategories);
    setSelectedProducts([]);
    const productsBySelectedCategories = categoriesTree.flatMap(category => {
      if (selectedCategories.includes(category.id)) {
        return Array.isArray(category?.products) ? category?.products : [];
      }
      const selectedSubCategories = Array.isArray(category?.subcategories)
        ? category?.subcategories.filter(subCategory =>
            selectedCategories.includes(subCategory?.id)
          )
        : [];
      return selectedSubCategories.flatMap(subCategory =>
        Array.isArray(subCategory?.products) ? subCategory?.products : []
      );
    });

    setFilterProductsCombo(mappedProducts(productsBySelectedCategories));
  };

  const handleSelectionChangeProduct = (e, optionsSelected) => {
    setSelectedProducts(optionsSelected);
  };

  const handleOpen = () => {
    setMobilePanelOpened(!mobilePanelOpened);
    if (isMobile && !mobilePanelOpened) {
      setShowFilters(false);
    }
  };

  const mobileFiltersApplied =
    isMobile && selectedOptions.length > 0 && !mobilePanelOpened;
  const locationName = getLocationName();
  const filterTitle = getFilterTitle();

  const content = (
    <div className={styles["auditoria-de-stock-content"]}>
      <div className={styles["auditoria-de-stock-filter-panel"]}>
        <AuditoriaFilterPanel
          handleApplyProp={handleApplyFilters}
          handleDeleteProp={handleDelete}
          opened={
            mobilePanelOpened ||
            !selectedOptions ||
            selectedOptions.length === 0
          }
          handleOpen={handleOpen}
          closeMobilePanel={handleOpen}
          handleSelectionChange={handleSelectionChange}
          stock={stockCountTotal}
          showFilters={showFilters}
          filterProducts={filterProductsCombo}
          handleSelectionChangeCategory={handleSelectionChangeCategory}
          handleSelectionChangeProduct={handleSelectionChangeProduct}
          selectedProducts={selectedProducts}
          categoriesTree={categoriesTree}
          clear={clear}
        />
      </div>
      {(!isMobile || !mobilePanelOpened) && (
        <>
          <div className={styles["auditoria-de-stock-reading-panel"]}>
            <ReadingPanel
              read={validatedItemsCount()}
              total={stockCountTotal}
              handleSearch={handleTagSearch}
              handleClear={handleClear}
              enabledRead={enabledRead}
              typeRead={"STOCK"}
              refRead={""}
              receiveEvent={receiveEvent}
            />
          </div>
          <div className={styles["auditoria-de-stock-table"]}>
            <div className={styles["auditoria-de-stock-table-header"]}>
              {!isMobile && originalStock.length > 0 && (
                <div
                  className={styles["auditoria-de-stock-table-header-count"]}
                >
                  {i18n.t("available.stock")}{" "}
                  <span>&nbsp;({stockCountTotal})</span>
                </div>
              )}
              {isMobile && !mobilePanelOpened && (
                <div
                  className={`${
                    styles["auditoria-de-stock-mobile-filter-icon"]
                  } ${
                    selectedOptions.length > 0
                      ? styles["table-products-active-filter"]
                      : ""
                  }`}
                  onClick={handleOpen}
                >
                  <i
                    className={`icon-iconos_filtros ${styles["table-products-top-icon"]}`}
                  ></i>
                  {mobileFiltersApplied && (
                    <div className={styles["mobile-filter-details"]}>
                      {locationName && (
                        <div className={styles["location-name"]}>
                          {locationName}
                        </div>
                      )}
                      {filterTitle && (
                        <div className={styles["category-product-name"]}>
                          {filterTitle}
                        </div>
                      )}
                      <div className={styles["stock-count-mobile"]}>
                        {i18n.t("stock")}&nbsp;
                        <span className={styles["stock-total-count"]}>
                          ({stockCountTotal})
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {!isMobile && originalStock.length > 0 && (
                <div className={styles["save-audit-button-container"]}>
                  <button
                    onClick={handleSaveAudit}
                    className={styles["save-audit-button"]}
                    title={i18n.t("save.audit")}
                  >
                    {i18n.t("save").toUpperCase()}
                  </button>
                </div>
              )}
              {searched && originalStock.length == 0 && !isMobile && (
                <div
                  className={`${styles["auditoria-de-stock-table-header-count"]} ${styles["full-width-center"]}`}
                >
                  <NotFoundItems />
                </div>
              )}
              {invalidTagsCount > 0 && (
                <div
                  className={
                    styles["auditoria-de-stock-table-header-invalid-tags"]
                  }
                >
                  {i18n.t("wrong.tags")} <span>({invalidTagsCount})</span>
                </div>
              )}
            </div>
            {groupedStock.length > 0 && (
              <ItemStockReadingTable
                stock={groupedStock}
                headers={headers}
                tagsInMov={tagsInMov}
                userId={userId}
                businessId={businessId}
                locationIds={locationIds}
                originalStock={originalStock}
                collectAuditDetails={collectAuditDetails}
              />
            )}
          </div>{" "}
        </>
      )}
      {isMobile && searched && stockCountTotal == 0 && (
        <div className={styles["auditoria-de-stock-table-header-count"]}>
          <NotFoundItems />
        </div>
      )}
    </div>
  );

  const headerButtons = (
    <Tooltip
      content={
        <div
          className={styles["excel-button"]}
          onClick={downloadReport}
          style={{
            width: "30px",
            cursor: tooltipStyle === "0.5" ? "default" : "pointer",
            display: "flex",
            alignItems: "center",
            opacity: tooltipStyle,
            pointerEvents: tooltipStyle === "0.5" ? "none" : "auto"
          }}
        >
          <img style={{ height: "30px" }} src={isMobile ? excelwhite : excel} />
        </div>
      }
      text={msgTooltip}
    ></Tooltip>
  );

  return (
    <div className={styles["auditoria-de-stock-container"]}>
      <AdminLayout
        headerTitle={i18n.t("stock.audit")}
        headerOptions={headerOptions}
        content={content}
        navHidden={true}
        goBackFunc={() => history.push("/admin/auditoria-de-stock")}
        customButtons={headerButtons}
      ></AdminLayout>

      <ErrorModal
        open={openErrorModal}
        message={errorMessage}
        handleGoBack={e => {
          setOpenErrorModal(false);
        }}
        handleClose={e => {
          setOpenErrorModal(false);
        }}
        handleButtonClick={e => {
          setOpenErrorModal(false);
        }}
      />

      {loading && <Loader />}
    </div>
  );
};

export default CreateStockAudit;
